// Helpers for rendering message content that may be either plain text
// (legacy + collapsed-composer output) or rich HTML (BC-23 Tiptap output).
// Backend already sanitizes via Loofah on save; DOMPurify here is defence
// in depth — same allowlist, ensures the bubble never trusts raw DB content.

import DOMPurify from 'dompurify'

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 's', 'code',
  'a', 'ul', 'ol', 'li', 'blockquote', 'img'
]

const ALLOWED_ATTR = ['href', 'src', 'alt', 'rel', 'target']

export function isHtmlContent(content: string): boolean {
  return /<[a-z][^>]*>/i.test(content)
}

export function sanitizeMessageHtml(content: string): string {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false
  })
}

// Hosts we never want to render a link-preview card for: our own product and
// (when known) the account's own website. A visitor's email reply usually
// quotes our branded notification, which links back to the account's site —
// previewing that just adds noise.
const BANSHEE_PREVIEW_HOSTS = ['banshee.chat']

function normalizeHost(host: string): string {
  return host.replace(/^www\./i, '').toLowerCase()
}

// Accepts a full URL or a bare host ("oldworldrankings.com") and returns its
// normalized host, or null if it can't be parsed.
export function hostFromWebsite(website?: string | null): string | null {
  if (!website) return null
  try {
    const withProtocol = /^https?:\/\//i.test(website) ? website : `https://${website}`
    return normalizeHost(new URL(withProtocol).hostname)
  } catch {
    return null
  }
}

export function shouldShowUrlPreview(url: string, accountWebsite?: string | null): boolean {
  let host: string
  try {
    host = normalizeHost(new URL(url).hostname)
  } catch {
    return false
  }
  if (!host) return false

  const accountHost = hostFromWebsite(accountWebsite)
  const suppressed = accountHost
    ? [accountHost, ...BANSHEE_PREVIEW_HOSTS]
    : BANSHEE_PREVIEW_HOSTS

  return !suppressed.some((s) => host === s || host.endsWith(`.${s}`))
}
